import React, {useState, useEffect} from "react"
import {GroupCelebration, GroupCelebrationStatus} from "@irati/types"
import authentication from "@irati/auth"
import modalStory, {anyStoriesOpen} from "@irati/utils/modalStory"
import History, {historyContext} from "@irati/utils/history"
import qs from "qs"
import GenericModal from "@irati/ui/src/GenericModal"
import RecognitionService from "@irati/recognition/services/Recognition"
import {useScheduledGroupCelebrationQuery} from "@irati/recognition/hooks"
import tracking from "@irati-tracking/recognition/recognition"
import {match as Match} from "react-router-dom"
import ErrorPopup from "../Error"
import SuccessMessage from "../CreateGroupCelebration/SuccessMessage"
import {RecognitionRoutes} from "../../../enums/Routes"
import ViewGroupCelebrationMessage from "./ViewGroupCelebrationMessage"
import InviteCollaboratorsPopup from "./InviteCollaborators"
import PublishErrorModal from "./PublishedErrorModal"
import RewardGroupCelebration from "../RewardGroupCelebration"
import SuccessManager from "../CreateRecognition/SuccessManager"
import {FeedItemType} from "@irati/recognition/enums"
export const INVITE_COLLABORATORS = "INVITE_COLLABORATORS"
export const VIEW_MESSAGE = "VIEW_MESSAGE"
export const SUCCESS_MESSAGE = "SUCCESS_MESSAGE"
export const SUCCESS_MESSAGE_EDITED = "SUCCESS_MESSAGE_EDITED"
export const REWARD_CELEBRATION = "REWARD_CELEBRATION"
export const SUCCESS_MANAGER = "SUCCESS_MANAGER"

const modals = [VIEW_MESSAGE, SUCCESS_MESSAGE, SUCCESS_MESSAGE_EDITED, INVITE_COLLABORATORS, REWARD_CELEBRATION, SUCCESS_MANAGER]

enum EditType {
  INVITE = "invite",
  REWARD = "reward"
}

interface Props {
  redirectToFeed?: boolean
  groupCelebration: GroupCelebration
  match: Match
}
const PopupMaps = {
  [EditType.INVITE]: INVITE_COLLABORATORS,
  [EditType.REWARD]: REWARD_CELEBRATION
}
const GroupCelebrationPopup: React.FC<Props> = (props, context) => {
  const {redirectToFeed, match} = props
  const {uuid: groupCelebrationId, editType} = match.params as {uuid: string; editType: EditType}
  const query = qs.parse(window.location.search.replace("?", ""))
  const {data: groupCelebration, loading, error} = useScheduledGroupCelebrationQuery({id: groupCelebrationId})
  const [show, setShow] = useState(modalStory(modals, PopupMaps[editType] || VIEW_MESSAGE))
  useEffect(() => {
    setShow(modalStory(modals, PopupMaps[editType] || VIEW_MESSAGE, show))
  }, [editType])
  const handleHide = () => {
    if (show.VIEW_MESSAGE) {
      tracking.recognitionCtaClick({
        source: "add_message_modal",
        type: "cancel"
      })
    }
    setShow(modalStory(modals, null))
    History.push(`${RecognitionRoutes.ACCOUNT}/my-group-celebrations`, context, {scrollToTop: false})
  }
  const handleAfterExit = () => {
    if (!anyStoriesOpen(show)) {
      if (redirectToFeed) {
        History.push(`${RecognitionRoutes.ACCOUNT}/my-group-celebrations`, context, {scrollToTop: false})
      }
    }
  }
  const onMessageCreated = () => {
    const isAuthor = authentication.user().uuid === groupCelebration?.author?.uuid
    const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")
    if (canReward && isAuthor && !groupCelebration?.rewardAmount) {
      setShow(modalStory(modals, REWARD_CELEBRATION, show))
      return
    }
    setShow(modalStory(modals, SUCCESS_MESSAGE, show))
  }
  const onMessageEdited = () => {
    setShow(modalStory(modals, SUCCESS_MESSAGE_EDITED, show))
  }
  const handleUnabledCompleteRequestHide = (groupCelebrationToRemove?: GroupCelebration) => {
    if (groupCelebrationToRemove) {
      RecognitionService.removeGroupCelebrationFromCache(groupCelebrationToRemove)
    }
    handleHide()
  }
  const onViewCelebration = () => {
    setShow(modalStory(modals, VIEW_MESSAGE, show))
  }
  const onRewardAttached = () => {
    tracking.rewardCreatedOnGreetingCard({
      source: "scheduled_greeting_card",
      type: "update_reward"
    })
    setShow(modalStory(modals, SUCCESS_MANAGER))
  }
  const onErrorReward = () => {
    tracking.rewardErrorOnGreetingCard({
      source: "scheduled_greeting_card",
      type: "update_reward"
    })
  }
  const onClickSkip = () => {
    handleHide()
  }
  const onSuccessManagerHide = () => {
    handleHide()
  }
  const onSuccessManagerCTAClick = () => {
    History.push("/polls/create-group", context)
  }
  if (loading) {
    return null
  }
  if (error) {
    return <ErrorPopup />
  }
  if (!loading && (!groupCelebration || groupCelebration.status !== GroupCelebrationStatus.Scheduled)) {
    return (
      <PublishErrorModal
        onHide={() => handleUnabledCompleteRequestHide(groupCelebration)}
        onConfirm={() => handleUnabledCompleteRequestHide(groupCelebration)}
      />
    )
  }
  return (
    <>
      <GenericModal show={show[VIEW_MESSAGE]} onHide={handleHide} onExited={handleAfterExit} hideActions>
        <ViewGroupCelebrationMessage
          groupCelebration={groupCelebration!}
          onMessageEdited={onMessageEdited}
          onMessageCreated={onMessageCreated}
        />
      </GenericModal>
      <GenericModal show={show[INVITE_COLLABORATORS]} onHide={handleHide} onExited={handleAfterExit} hideActions>
        <InviteCollaboratorsPopup groupCelebration={groupCelebration!} />
      </GenericModal>
      <GenericModal show={show[SUCCESS_MESSAGE] || show[SUCCESS_MESSAGE_EDITED]} small onHide={handleHide} hideActions>
        <SuccessMessage
          scheduledGroupCelebration={groupCelebration!}
          onHide={handleHide}
          onViewCelebration={onViewCelebration}
          isEdit={show[SUCCESS_MESSAGE_EDITED]}
        />
      </GenericModal>
      <GenericModal customWidth="800" show={show[REWARD_CELEBRATION]} onHide={handleHide} onExited={handleAfterExit} hideActions>
        <RewardGroupCelebration
          groupCelebration={groupCelebration!}
          onErrorReward={onErrorReward}
          onClickButton={onClickSkip}
          onRewardCreated={onRewardAttached}
          isMessageAdded={show.previous?.[VIEW_MESSAGE] && query.edit !== "true"}
        />
      </GenericModal>
      <GenericModal show={show[SUCCESS_MANAGER]} onHide={onSuccessManagerHide} onExited={handleAfterExit} hideActions>
        <SuccessManager onClickButton={onSuccessManagerCTAClick} onHide={onSuccessManagerHide} type={FeedItemType.GROUP_RECOGNITION} />
      </GenericModal>
    </>
  )
}

GroupCelebrationPopup.contextTypes = historyContext

export default GroupCelebrationPopup
